import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {
    iso8601DateTimeZodValidator,
    uuidZodValidator,
} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    ottopilotEventContainerCudLogValidator,
    ottopilotEventContainerValidator,
    ottopilotEventContainerWithEventsValidator,
    type OttopilotEventContainer,
    type OttopilotEventContainerCudLog,
    type OttopilotEventContainerWithEvents,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {ComplexFilter} from "@vesta/vos-types/modules/workspaceLevel/domain/ComplexFilter";
import {z as zod} from "zod";

type EventContainerApiContext = {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
};

type EventContainerOrderBy = Array<{
    column: string;
    direction: "asc" | "desc";
}>;

type EventContainerQuery = EventContainerApiContext & {
    columns?: Array<string>;
    filters?: ComplexFilter;
    id?: Uuid;
    limit?: number;
    offset?: number;
    orderBy?: EventContainerOrderBy;
    templateId: Uuid;
};

type EventContainerWithEventsQuery = EventContainerQuery & {
    eventColumns?: Array<string>;
};

export type EventContainerNoteInput = {
    attachments: Array<{
        assetId: Uuid;
        createdAt: Iso8601DateTime;
        fileName: string;
        mimeType?: string | null;
    }>;
    bodyMarkdown: string;
    scopeType?: "eventContainer";
};

export type EventContainerNote = {
    attachments: Array<{
        assetId: Uuid;
        createdAt: Iso8601DateTime;
        fileName: string;
        mimeType: string | null;
    }>;
    bodyMarkdown: string;
    bodyPlainTextLength: number;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    eventContainerId: Uuid;
    eventId: Uuid | null;
    eventTemplateId: Uuid | null;
    id: Uuid;
    scopeType: "event" | "eventContainer";
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
};

const eventContainerNoteInputValidator = zod.strictObject({
    attachments: zod.array(
        zod.strictObject({
            assetId: uuidZodValidator,
            createdAt: iso8601DateTimeZodValidator,
            fileName: zod.string(),
            mimeType: zod.string().nullable().optional(),
        }),
    ),
    bodyMarkdown: zod.string(),
    scopeType: zod.literal("eventContainer").optional(),
}) satisfies zod.ZodType<EventContainerNoteInput>;

const eventContainerNoteValidator = zod.strictObject({
    attachments: zod.array(
        zod.strictObject({
            assetId: uuidZodValidator,
            createdAt: iso8601DateTimeZodValidator,
            fileName: zod.string(),
            mimeType: zod.string().nullable(),
        }),
    ),
    bodyMarkdown: zod.string(),
    bodyPlainTextLength: zod.number(),
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    eventContainerId: uuidZodValidator,
    eventId: uuidZodValidator.nullable(),
    eventTemplateId: uuidZodValidator.nullable(),
    id: uuidZodValidator,
    scopeType: zod.enum(["event", "eventContainer"]),
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
}) satisfies zod.ZodType<EventContainerNote>;

function getHeaders({accessToken, workspaceId}: Pick<EventContainerApiContext, "accessToken" | "workspaceId">): {
    Authorization: string;
    "Content-Type": string;
    "X-Workspace-Id": Uuid;
} {
    return {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "X-Workspace-Id": workspaceId,
    };
}

function getEventContainerQueryBody(query: EventContainerQuery): Record<string, unknown> {
    return {
        ...(query.columns === undefined ? {} : {columns: query.columns}),
        ...(query.filters === undefined ? {} : {filters: query.filters}),
        ...(query.id === undefined ? {} : {id: query.id}),
        ...(query.limit === undefined ? {} : {limit: query.limit}),
        ...(query.offset === undefined ? {} : {offset: query.offset}),
        ...(query.orderBy === undefined ? {} : {orderBy: query.orderBy}),
        templateId: query.templateId,
    };
}

function getFilterScopedQueryBody(
    query: Pick<EventContainerQuery, "filters" | "id" | "limit" | "offset" | "orderBy" | "templateId">,
): Record<string, unknown> {
    return {
        ...(query.filters === undefined ? {} : {filters: query.filters}),
        ...(query.id === undefined ? {} : {id: query.id}),
        ...(query.limit === undefined ? {} : {limit: query.limit}),
        ...(query.offset === undefined ? {} : {offset: query.offset}),
        ...(query.orderBy === undefined ? {} : {orderBy: query.orderBy}),
        templateId: query.templateId,
    };
}

function getEventContainerWithEventsQueryBody(query: EventContainerWithEventsQuery): Record<string, unknown> {
    return {
        ...getEventContainerQueryBody(query),
        ...(query.eventColumns === undefined ? {} : {eventColumns: query.eventColumns}),
    };
}

export async function getEventContainers(query: EventContainerQuery): Promise<Result<Array<OttopilotEventContainer>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/get-all`,
        {
            body: JSON.stringify(getEventContainerQueryBody(query)),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.array(ottopilotEventContainerValidator),
    );
}

export async function countEventContainers(
    query: Pick<EventContainerQuery, "accessToken" | "filters" | "id" | "intellsysBaseUrl" | "templateId" | "workspaceId">,
): Promise<Result<number>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/count-all`,
        {
            body: JSON.stringify({
                ...(query.filters === undefined ? {} : {filters: query.filters}),
                ...(query.id === undefined ? {} : {id: query.id}),
                templateId: query.templateId,
            }),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.number(),
    );
}

export async function getEventContainerColumnValues(
    query: EventContainerApiContext & {
        column: string;
        filters?: ComplexFilter;
        templateId: Uuid;
    },
): Promise<Result<Array<string | null>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/column-values`,
        {
            body: JSON.stringify({
                column: query.column,
                ...(query.filters === undefined ? {} : {filters: query.filters}),
                templateId: query.templateId,
            }),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.array(zod.string().nullable()),
    );
}

export async function createEventContainer(
    command: EventContainerApiContext & {
        data: Record<string, unknown>;
        templateId: Uuid;
    },
): Promise<Result<Uuid>> {
    return await robustJsonResultClientFetch(
        `${command.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/create`,
        {
            body: JSON.stringify({
                data: command.data,
                templateId: command.templateId,
            }),
            headers: getHeaders(command),
            method: "POST",
        },
        uuidZodValidator,
    );
}

export async function updateEventContainer(
    command: EventContainerApiContext & {
        data: Record<string, unknown>;
        id: Uuid;
        templateId: Uuid;
    },
): Promise<Result<void>> {
    return await robustJsonResultClientFetch(
        `${command.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/update`,
        {
            body: JSON.stringify({
                data: command.data,
                id: command.id,
                templateId: command.templateId,
            }),
            headers: getHeaders(command),
            method: "POST",
        },
        zod.undefined(),
    );
}

export async function getEventContainerCudLogs(
    query: EventContainerApiContext & {
        filters?: ComplexFilter;
        id?: Uuid;
        limit?: number;
        offset?: number;
        orderBy?: EventContainerOrderBy;
        templateId: Uuid;
    },
): Promise<Result<Array<OttopilotEventContainerCudLog>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/cud/logs/get`,
        {
            body: JSON.stringify(getFilterScopedQueryBody(query)),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.array(ottopilotEventContainerCudLogValidator),
    );
}

export const LAST_6_DISPOSITION_CHANGES_LIMIT = 6;

export async function getLastNDispositionIdsForEventContainer(
    query: EventContainerApiContext & {
        dispositionColumnId: Uuid;
        eventContainerId: Uuid;
        lastNDispositionChangesLimit?: number;
        templateId: Uuid;
    },
): Promise<Result<Array<Uuid>>> {
    if (query.templateId === undefined) {
        return errorResult(new Error("Event container template id is required"));
    }
    const logsResult = await getEventContainerCudLogs({
        accessToken: query.accessToken,
        filters: {
            logic: "AND",
            filters: [{
                column: "id",
                operator: "equals",
                value: query.eventContainerId,
            }],
        },
        limit: 9999,
        offset: 0,
        orderBy: [{column: "updatedAt", direction: "asc"}],
        intellsysBaseUrl: query.intellsysBaseUrl,
        templateId: query.templateId,
        workspaceId: query.workspaceId,
    });
    if (logsResult.success !== true) {
        return logsResult;
    }

    const chronologicalLogs = [...logsResult.data].sort((left, right) => {
        const timestampComparison = left.updatedAt.localeCompare(right.updatedAt);
        return timestampComparison === 0
            ? left.revisionId.localeCompare(right.revisionId)
            : timestampComparison;
    });
    const changedDispositionIds: Array<Uuid> = [];
    for (let index = 1; index < chronologicalLogs.length; index += 1) {
        const previousLog = chronologicalLogs[index - 1];
        const currentLog = chronologicalLogs[index];
        if (previousLog === undefined || currentLog === undefined) {
            continue;
        }
        const previousDispositionResult = uuidZodValidator.safeParse(
            previousLog.additionalColumnValues[query.dispositionColumnId],
        );
        const currentDispositionResult = uuidZodValidator.safeParse(
            currentLog.additionalColumnValues[query.dispositionColumnId],
        );
        if (previousDispositionResult.success !== true || currentDispositionResult.success !== true) {
            return errorResult(new Error(
                `Event Container CUD log is missing configured disposition column ${query.dispositionColumnId}`,
            ));
        }
        if (previousDispositionResult.data !== currentDispositionResult.data) {
            changedDispositionIds.push(currentDispositionResult.data);
        }
    }

    return dataResult(
        changedDispositionIds
            .reverse()
            .slice(0, query.lastNDispositionChangesLimit ?? LAST_6_DISPOSITION_CHANGES_LIMIT),
    );
}

export async function countEventContainerCudLogs(
    query: Pick<EventContainerQuery, "accessToken" | "filters" | "intellsysBaseUrl" | "templateId" | "workspaceId">,
): Promise<Result<number>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/cud/logs/count`,
        {
            body: JSON.stringify(getFilterScopedQueryBody(query)),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.number(),
    );
}

export async function getEventContainersWithEvents(
    query: EventContainerWithEventsQuery,
): Promise<Result<Array<OttopilotEventContainerWithEvents>>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/events/get-all`,
        {
            body: JSON.stringify(getEventContainerWithEventsQueryBody(query)),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.array(ottopilotEventContainerWithEventsValidator),
    );
}

export async function countEventContainersWithEvents(
    query: Pick<EventContainerQuery, "accessToken" | "filters" | "id" | "intellsysBaseUrl" | "templateId" | "workspaceId">,
): Promise<Result<number>> {
    return await robustJsonResultClientFetch(
        `${query.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/events/count-all`,
        {
            body: JSON.stringify(getFilterScopedQueryBody(query)),
            headers: getHeaders(query),
            method: "POST",
        },
        zod.number(),
    );
}

export async function createEventContainerNote(
    command: EventContainerApiContext & {
        eventContainerId: Uuid;
        note: EventContainerNoteInput;
        templateId: Uuid;
    },
): Promise<Result<{
    eventContainerId: Uuid;
    note: EventContainerNote;
}>> {
    return await robustJsonResultClientFetch(
        `${command.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/notes/create`,
        {
            body: JSON.stringify({
                eventContainerId: command.eventContainerId,
                note: eventContainerNoteInputValidator.parse(command.note),
                templateId: command.templateId,
            }),
            headers: getHeaders(command),
            method: "POST",
        },
        zod.strictObject({
            eventContainerId: uuidZodValidator,
            note: eventContainerNoteValidator,
        }),
    );
}

export async function updateEventContainerNote(
    command: EventContainerApiContext & {
        eventContainerId: Uuid;
        note: EventContainerNoteInput;
        noteId: Uuid;
        templateId: Uuid;
    },
): Promise<Result<{
    eventContainerId: Uuid;
    note: EventContainerNote;
}>> {
    return await robustJsonResultClientFetch(
        `${command.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/notes/update`,
        {
            body: JSON.stringify({
                eventContainerId: command.eventContainerId,
                note: eventContainerNoteInputValidator.parse(command.note),
                noteId: command.noteId,
                templateId: command.templateId,
            }),
            headers: getHeaders(command),
            method: "POST",
        },
        zod.strictObject({
            eventContainerId: uuidZodValidator,
            note: eventContainerNoteValidator,
        }),
    );
}

export async function deleteEventContainerNote(
    command: EventContainerApiContext & {
        eventContainerId: Uuid;
        noteId: Uuid;
        templateId: Uuid;
    },
): Promise<Result<{
    eventContainerId: Uuid;
    noteId: Uuid;
}>> {
    return await robustJsonResultClientFetch(
        `${command.intellsysBaseUrl}/sub-system/pluto/ottopilot/event-containers/notes/delete`,
        {
            body: JSON.stringify({
                eventContainerId: command.eventContainerId,
                noteId: command.noteId,
                templateId: command.templateId,
            }),
            headers: getHeaders(command),
            method: "POST",
        },
        zod.strictObject({
            eventContainerId: uuidZodValidator,
            noteId: uuidZodValidator,
        }),
    );
}
